// SPDX-License-Identifier: MIT
/** Light: glows, specular sweeps across shapes and type, soft light pools. */
import { rgba } from '../brand.ts';
import { layer } from './layers.ts';
type Ctx = CanvasRenderingContext2D;
/** Soft radial light pool. */
export function pool(ctx: Ctx, x: number, y: number, r: number, color: string, alpha = 1): void {
    if (alpha <= 0)
        return;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, rgba(color, alpha));
    g.addColorStop(0.35, rgba(color, alpha * 0.35));
    g.addColorStop(1, rgba(color, 0));
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
}
/**
 * Specular sweep: a bright band crosses the area `shape` paints (any colour; it is used as a mask
 * only) and is added on top with 'lighter'. `progress` 0→1 moves
 * the band across `box`; `angle` in radians; `width` of the band in px.
 */
export function sweep(ctx: Ctx, shape: (l: Ctx) => void, box: {
    x: number;
    y: number;
    w: number;
    h: number;
}, progress: number, { angle = -0.35, width = 160, color = '#FFFFFF', intensity = 0.9 } = {}): void {
    if (progress <= 0 || progress >= 1)
        return;
    layer(ctx, (l) => {
        // `shape` only defines where the band may appear: keep the band, drop the shape's own pixels.
        shape(l);
        l.globalCompositeOperation = 'source-in';
        const span = box.w + width * 2;
        const cx = box.x - width + span * progress;
        const cy = box.y + box.h / 2;
        const dx = Math.cos(angle) * width;
        const dy = Math.sin(angle) * width;
        const g = l.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
        g.addColorStop(0, rgba(color, 0));
        g.addColorStop(0.5, rgba(color, intensity));
        g.addColorStop(1, rgba(color, 0));
        l.fillStyle = g;
        l.fillRect(box.x - width, box.y - width, box.w + width * 2, box.h + width * 2);
    }, { blend: 'lighter' });
}
/** Draw something with a coloured glow halo (shadow-based, cheap). */
export function glow(ctx: Ctx, draw: (c: Ctx) => void, { color = '#FFFFFF', blur = 30, strength = 1 } = {}): void {
    ctx.save();
    ctx.shadowColor = rgba(color, Math.min(1, strength));
    ctx.shadowBlur = blur;
    draw(ctx);
    ctx.restore();
}
